import React from 'react';
import { Typography, Card, Row, Col, Form, Button, Switch, Select, Tabs, Divider } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../hooks/useTranslationWithNamespace';
import SearchableSelect from '../components/common/SearchableSelect';

const { Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const SettingsPage: React.FC = () => {
  const { t } = useTranslationWithNamespace('settings');
  const [generalForm] = Form.useForm();
  const [securityForm] = Form.useForm();
  const [notificationForm] = Form.useForm();

  // Handle saving general settings
  const handleSaveGeneralSettings = (values: any) => {
    console.log('General settings:', values);
    // TODO: Implement API call to save settings
  };

  // Handle saving security settings
  const handleSaveSecuritySettings = (values: any) => {
    console.log('Security settings:', values);
    // TODO: Implement API call to save settings
  };

  // Handle saving notification settings
  const handleSaveNotificationSettings = (values: any) => {
    console.log('Notification settings:', values);
    // TODO: Implement API call to save settings
  };

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={2}>{t('navigation.settings', 'Settings')}</Title>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        <Tabs defaultActiveKey="general">
          <TabPane tab={t('settings.general', 'General')} key="general">
            <Form
              form={generalForm}
              layout="vertical"
              onFinish={handleSaveGeneralSettings}
              initialValues={{
                language: 'en',
                theme: 'light',
                dateFormat: 'MM/DD/YYYY',
                timeFormat: '24h'
              }}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="language"
                    label={t('settings.language', 'Language')}
                    rules={[{ required: true, message: t('settings.languageRequired', 'Please select a language') }]}
                  >
                    <SearchableSelect searchPlaceholder="Type to search...">
                      <Option value="en">English</Option>
                      <Option value="fa">فارسی</Option>
                    </SearchableSelect>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="theme"
                    label={t('settings.theme', 'Theme')}
                    rules={[{ required: true, message: t('settings.themeRequired', 'Please select a theme') }]}
                  >
                    <SearchableSelect searchPlaceholder="Type to search...">
                      <Option value="light">{t('settings.themeLight', 'Light')}</Option>
                      <Option value="dark">{t('settings.themeDark', 'Dark')}</Option>
                      <Option value="system">{t('settings.themeSystem', 'System Default')}</Option>
                    </SearchableSelect>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="dateFormat"
                    label={t('settings.dateFormat', 'Date Format')}
                    rules={[{ required: true, message: t('settings.dateFormatRequired', 'Please select a date format') }]}
                  >
                    <SearchableSelect searchPlaceholder="Type to search...">
                      <Option value="MM/DD/YYYY">MM/DD/YYYY</Option>
                      <Option value="DD/MM/YYYY">DD/MM/YYYY</Option>
                      <Option value="YYYY-MM-DD">YYYY-MM-DD</Option>
                    </SearchableSelect>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="timeFormat"
                    label={t('settings.timeFormat', 'Time Format')}
                    rules={[{ required: true, message: t('settings.timeFormatRequired', 'Please select a time format') }]}
                  >
                    <SearchableSelect searchPlaceholder="Type to search...">
                      <Option value="12h">{t('settings.timeFormat12h', '12-hour (AM/PM)')}</Option>
                      <Option value="24h">{t('settings.timeFormat24h', '24-hour')}</Option>
                    </SearchableSelect>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Row>
                <Col span={24} style={{ textAlign: 'right' }}>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                    {t('actions.save', 'Save')}
                  </Button>
                </Col>
              </Row>
            </Form>
          </TabPane>

          <TabPane tab={t('settings.security', 'Security')} key="security">
            <Form
              form={securityForm}
              layout="vertical"
              onFinish={handleSaveSecuritySettings}
              initialValues={{
                twoFactorAuth: false,
                sessionTimeout: 30,
                passwordExpiry: 90
              }}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="twoFactorAuth"
                    label={t('settings.twoFactorAuth', 'Two-Factor Authentication')}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="sessionTimeout"
                    label={t('settings.sessionTimeout', 'Session Timeout (minutes)')}
                    rules={[{ required: true, message: t('settings.sessionTimeoutRequired', 'Please enter session timeout') }]}
                  >
                    <SearchableSelect searchPlaceholder="Type to search...">
                      <Option value={15}>15</Option>
                      <Option value={30}>30</Option>
                      <Option value={60}>60</Option>
                      <Option value={120}>120</Option>
                    </SearchableSelect>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="passwordExpiry"
                    label={t('settings.passwordExpiry', 'Password Expiry (days)')}
                    rules={[{ required: true, message: t('settings.passwordExpiryRequired', 'Please enter password expiry') }]}
                  >
                    <SearchableSelect searchPlaceholder="Type to search...">
                      <Option value={30}>30</Option>
                      <Option value={60}>60</Option>
                      <Option value={90}>90</Option>
                      <Option value={180}>180</Option>
                      <Option value={365}>365</Option>
                    </SearchableSelect>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Row>
                <Col span={24} style={{ textAlign: 'right' }}>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                    {t('actions.save', 'Save')}
                  </Button>
                </Col>
              </Row>
            </Form>
          </TabPane>

          <TabPane tab={t('settings.notifications', 'Notifications')} key="notifications">
            <Form
              form={notificationForm}
              layout="vertical"
              onFinish={handleSaveNotificationSettings}
              initialValues={{
                emailNotifications: true,
                rpcAlerts: true,
                transactionAlerts: true,
                systemAlerts: true
              }}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="emailNotifications"
                    label={t('settings.emailNotifications', 'Email Notifications')}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="rpcAlerts"
                    label={t('settings.rpcAlerts', 'RPC Status Alerts')}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="transactionAlerts"
                    label={t('settings.transactionAlerts', 'Transaction Alerts')}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="systemAlerts"
                    label={t('settings.systemAlerts', 'System Alerts')}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Row>
                <Col span={24} style={{ textAlign: 'right' }}>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                    {t('actions.save', 'Save')}
                  </Button>
                </Col>
              </Row>
            </Form>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default SettingsPage;
